import React from "react";
import { Box, Flex, VStack, Text } from "@chakra-ui/react";
import { _get } from "../../../utils/request";
import { getPaymentFlags } from "../../../utils/paymentFlags";
import { loadCatalog } from "../../../i18n";
import { i18n } from "@lingui/core";
import { msg, t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import Head from "../../../components/Head";
import Button from "../../../components/Button";
import { useRouter } from "next/router";
import Image from "next/image";
import { useUserInfo } from "../../../hooks/useUserInfo";
import { useFreshStreamerData } from "../../../hooks/useFreshStreamerData";
import { useWishlistDetailPublic } from "../../../hooks/useWishlistDetailPublic";
import { DonationForm } from "../../../views/DonationForm";
import { VoucherForm } from "../../../views/VoucherForm";
import { WishlistForm } from "../../../views/WishlistForm";
import { SoundboardForm } from "../../../views/SoundboardForm";
import { TokoForm } from "../../../views/TokoForm";
import {
  StreamerPageLayout,
  useStreamerTabs,
  getPaymentFlagsFromData,
} from "../../../views/StreamerPage/StreamerPageLayout";
import copyHappy from "../../../assets/capy_happy.svg";

export async function getStaticPaths() {
  return { paths: [], fallback: true };
}

export async function getStaticProps({ params, ...ctx }) {
  const translation = await loadCatalog(ctx.locale);
  const revalidate = { revalidate: 5 };
  const paymentFlags = await getPaymentFlags();
  const { username, product_slug } = params;

  i18n.loadAndActivate({ locale: ctx.locale, messages: translation });

  try {
    const {
      json: { data: apiData },
    } = await _get(`users/${username}`);

    return {
      props: {
        data: { ...apiData, ...paymentFlags, product_slug },
        translation,
      },
      ...revalidate,
    };
  } catch (error) {
    if (error.response) {
      const { status, json } = error.response;
      return {
        props: { data: { username, product_slug }, error: { json, status }, translation },
        revalidate: 1,
      };
    }
    return {
      props: {
        data: { username, product_slug },
        translation,
        error: { message: "UNKNOWN_ERROR", status: 500 },
      },
      revalidate: 1,
    };
  }
}

const WishlistDetailPage = ({ data: staticData = {}, error }) => {
  const { data } = useFreshStreamerData(staticData, error);
  const router = useRouter();
  const { data: userInfo = {} } = useUserInfo({}, () => {});
  const { username: currUsername, email: currEmail } = userInfo;
  const { _ } = useLingui();
  const {
    username,
    id,
    plus,
    overlay = {},
    vote = {},
    currency,
    transaction_fee_policy: transactionFeePolicy,
    product_slug,
  } = data;

  const { isTopUp, gameVoucherEnabled, hasWishlist, soundboard, hasStore } = useStreamerTabs(data);
  const paymentFlags = getPaymentFlagsFromData(data);

  const { data: wishlistDetail, isError } = useWishlistDetailPublic(product_slug, username);

  // Wishlist not found — show custom empty state
  if (!error && (isError || !wishlistDetail)) {
    return (
      <StreamerPageLayout data={data} error={error} skipTabs>
        <Box
          w="100%"
          boxShadow={"9.6px 9.6px 0px #222"}
          borderRadius="6px"
          bg="#f2f7f5"
          border={"0.7px solid #000"}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          p="25px 20px"
          gap="20px"
          fontSize="20px"
          color="#000"
          fontFamily="Comfortaa"
          mt="30px"
        >
          <VStack p={8} spacing={4} w={"100%"} mx={"auto"}>
            <Flex
              flexDir="column"
              align="center"
              justify="center"
              w="full"
              mx="auto"
              py={20}
              gap={4}
              color="gray.700"
              fontFamily="'IBM Plex Mono'"
            >
              <Box w="153px">
                <Image src={copyHappy} alt="empty" boxSize="153px" mb={4} />
              </Box>
              <Text fontSize="lg" fontWeight="500" textAlign="center">
                <Trans>Wishlist lagi nggak tersedia, nih!</Trans>
              </Text>
              <Text fontSize="sm" textAlign="center" color="gray.500">
                <Trans>Kalau wishlist udah ada, langsung tampil di sini kok</Trans>
              </Text>
              <Button
                mt={6}
                bg="#96D2D9"
                border="1px solid black"
                _hover={{ bg: "#7fbfc7" }}
                onClick={() => router.push(`/${username}`)}
                label={_(msg`Lihat Halaman Lainnya`)}
              />
            </Flex>
          </VStack>
        </Box>
      </StreamerPageLayout>
    );
  }

  const head = wishlistDetail ? (
    <Head title={t`Dukung ${username} mendapatkan ${wishlistDetail.product_name} di Saweria`}>
      <meta name="description" content={t`Bantu ${username} wujudkan barang impiannya ${wishlistDetail.product_name} lewat dukungan di Saweria. Caranya gampang dan seru!`} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://saweria.co/${username}/wishlist/${product_slug}`} />
      <meta property="og:title" content={t`Dukung ${username} mendapatkan ${wishlistDetail.product_name} di Saweria`} />
      <meta property="og:description" content={t`Bantu ${username} wujudkan barang impiannya ${wishlistDetail.product_name} lewat dukungan di Saweria. Caranya gampang dan seru!`} />
      <meta property="og:image" content={wishlistDetail.image_url || "https://saweria.co/twitter_card.png"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@saweriaco" />
      <meta name="twitter:title" content={t`Dukung ${username} mendapatkan ${wishlistDetail.product_name} di Saweria`} />
      <meta name="twitter:description" content={t`Bantu ${username} wujudkan barang impiannya ${wishlistDetail.product_name} lewat dukungan di Saweria. Caranya gampang dan seru!`} />
      <meta name="twitter:image" content={wishlistDetail.image_url || "https://saweria.co/twitter_card.png"} />
    </Head>
  ) : null;

  const panels = [];

  if (!isTopUp) {
    panels.push(
      <DonationForm
        key="donation"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
        challenge={data.challenge}
      />
    );
  }

  if (isTopUp || gameVoucherEnabled) {
    panels.push(
      <VoucherForm
        key="voucher"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && hasStore) {
        panels.push(
          <TokoForm
            key="toko"
            plus={plus}
            overlay={overlay}
            vote={vote}
            streamerId={id}
            {...paymentFlags}
            currEmail={currEmail}
            currUsername={currUsername}
            streamerUsername={username}
            currency={currency}
            transactionFeePolicy={transactionFeePolicy}
          />
        );
    }

  if (!isTopUp && hasWishlist) {
    panels.push(
      wishlistDetail ? (
        <WishlistForm
          key="wishlist"
          wishlist={wishlistDetail}
          plus={plus}
          overlay={overlay}
          vote={vote}
          streamerId={id}
          {...paymentFlags}
          currEmail={currEmail}
          currUsername={currUsername}
          streamerUsername={username}
          currency={currency}
          transactionFeePolicy={transactionFeePolicy}
        />
      ) : null
    );
  }

  if (!isTopUp && soundboard) {
    panels.push(
      <SoundboardForm
        key="soundboard"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  return (
    <StreamerPageLayout data={data} error={error} head={head}>
      {panels}
    </StreamerPageLayout>
  );
};

export default WishlistDetailPage;
